import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { getPageContent } from '../utils/contentManager';

const Contact = () => {
  const [pageContent, setPageContent] = useState({});
  const [contactInfo, setContactInfo] = useState([]);

  useEffect(() => {
    const content = getPageContent('contact');
    setPageContent(content);

    // Dynamic contact info
    const { getContactInfo } = require('../utils/contentManager');
    const dynamicContactInfo = getContactInfo();

    setContactInfo([
      {
        icon: Mail,
        title: 'Email Us',
        details: dynamicContactInfo.email || 'contact@ohasystem.com',
        subtitle: 'We respond within 24 hours'
      },
      {
        icon: Phone,
        title: 'Call Us',
        details: dynamicContactInfo.phone || '+1 (555) 123-4567',
        subtitle: dynamicContactInfo.businessHours || 'Mon-Fri, 9AM-6PM PST'
      },
      {
        icon: MapPin,
        title: 'Visit Us',
        details: dynamicContactInfo.address || 'San Francisco, CA',
        subtitle: 'Schedule an appointment'
      },
      {
        icon: Clock,
        title: 'Business Hours',
        details: dynamicContactInfo.businessHours || 'Mon-Fri: 9AM-6PM PST',
        subtitle: 'Weekend consultations available'
      }
    ]);
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const services = [
    'Agentic AI Solutions',
    'IT Consulting',
    'Business Strategy',
    'Digital Transformation',
    'Process Automation',
    'Other'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitted(false);

    try {
      const response = await fetch('http://localhost:5000/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setIsSubmitted(true);

        // Reset form after 3 seconds
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({
            name: '',
            email: '',
            company: '',
            phone: '',
            service: '',
            message: ''
          });
        }, 3000);
      } else {
        console.error("Failed to send form data.");
      }
    } catch (error) {
      console.error("Error sending form data:", error);
    }
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 102, 255, 0.9), rgba(0, 51, 153, 0.9)), url('https://images.unsplash.com/photo-1495576775051-8af0d10f19b1?w=1920&h=1080&fit=crop')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              {pageContent.title || 'Contact'} <span className="text-indigo-200">Us</span>
            </h1>
            <p className="text-xl md:text-2xl max-w-4xl mx-auto leading-relaxed text-indigo-100">
              {pageContent.subtitle ||
                "Ready to transform your business? Let's start the conversation about how our AI solutions can drive your success."}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              {pageContent.contactTitle || 'Get in'} <span className="text-gradient">Touch</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {pageContent.contactSubtitle || 'Multiple ways to reach us - choose what works best for you.'}
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                whileHover={{ y: -10 }}
                className="text-center p-6 glass-effect rounded-2xl hover:shadow-glow transition-all duration-300"
              >
                <div className="gradient-background w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <info.icon size={28} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{info.title}</h3>
                <p className="text-gray-700 font-medium mb-1">{info.details}</p>
                <p className="text-gray-500 text-sm">{info.subtitle}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-white p-8 rounded-3xl shadow-premium"
            >
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                {pageContent.formTitle || 'Get Your Free Basic Consultation'}
              </h3>
              <p className="text-gray-600 mb-8">
                {pageContent.formSubtitle ||
                  "Fill out the form below and we'll get back to you within 24 hours to discuss how we can help transform your business."}
              </p>

              {isSubmitted ? (
                <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-12">
                  <CheckCircle size={64} className="text-green-500 mx-auto mb-4" />
                  <h4 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h4>
                  <p className="text-gray-600">
                    Your message has been sent successfully. We'll be in touch soon!
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-transparent transition-all duration-300"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-transparent transition-all duration-300"
                        placeholder="john@company.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Company Name
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-transparent transition-all duration-300"
                        placeholder="Your Company"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-transparent transition-all duration-300"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Service Interest
                    </label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-transparent transition-all duration-300"
                    >
                      <option value="">Select a service</option>
                      {services.map((service) => (
                        <option key={service} value={service}>
                          {service}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-transparent transition-all duration-300 resize-none"
                      placeholder="Tell us about your business challenges and goals..."
                    />
                  </div>

                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full gradient-background text-white py-4 rounded-xl font-bold text-lg shadow-premium hover:shadow-glow transition-all duration-300 flex items-center justify-center space-x-2"
                  >
                    <span>Send Message</span>
                    <Send size={20} />
                  </motion.button>
                </form>
              )}
            </motion.div>

            {/* Side Info */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6">
                  {pageContent.whyChooseTitle || 'Why Choose OHA System?'}
                </h3>
                <div className="space-y-6">
                  {[
                    {
                      title: 'Free Initial Consultation',
                      description: 'Get expert advice on your AI and technology strategy at no cost.'
                    },
                    {
                      title: 'Rapid Response',
                      description: 'We respond to all inquiries within 24 hours, often much sooner.'
                    },
                    {
                      title: 'Tailored Solutions',
                      description: 'Every solution is customized to your specific business needs and goals.'
                    },
                    {
                      title: 'Proven Track Record',
                      description: '99.9% client satisfaction rate with measurable ROI improvements.'
                    }
                  ].map((item, index) => (
                    <div key={index} className="flex space-x-4">
                      <div className="w-6 h-6 bg-blue-800 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <CheckCircle size={16} className="text-white" />
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-1">{item.title}</h4>
                        <p className="text-gray-600">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="glass-effect p-6 rounded-2xl">
                <h4 className="text-xl font-bold text-gray-900 mb-4">
                  {pageContent.nextStepsTitle || 'What Happens Next?'}
                </h4>
                <div className="space-y-3">
                  {[
                    "We review your information and goals",
                    "Schedule a free consultation call",
                    "Develop a customized strategy proposal"
                  ].map((step, i) => (
                    <div key={i} className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-800 rounded-full flex items-center justify-center text-white text-sm font-bold">
                        {i + 1}
                      </div>
                      <span className="text-gray-700">{step}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
